import { db } from "./db";
import { writeBlob } from "./opfs";
import { ProviderError } from "./providers/types";
import type { GmailClient, OutlookClient, MailProviderClient, NormalizedMessage, NormalizedAttachment } from "./providers/types";
import type { Document, Occurrence, SyncState, ReviewItem } from "@/types";

function randomId(): string {
  return crypto.randomUUID();
}

function getExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  return lastDot > 0 ? filename.slice(lastDot + 1).toLowerCase() : "";
}

function getMimeType(filename: string): string {
  const ext = getExtension(filename);
  const map: Record<string, string> = {
    pdf: "application/pdf",
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    webp: "image/webp",
    svg: "image/svg+xml",
    txt: "text/plain",
    csv: "text/csv",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    zip: "application/zip",
  };
  return map[ext] || "application/octet-stream";
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function hashBlob(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function findDuplicate(contentHash: string): Promise<Document | undefined> {
  return db.documents.where("contentHash").equals(contentHash).first();
}

function shouldCreateReviewItem(
  doc: Document,
  _senderEmail: string | undefined,
  isDuplicate: boolean
): ReviewItem | null {
  const reasons: ReviewItem["reason"][] = [];

  if (isDuplicate) reasons.push("duplicate");
  if (doc.sizeBytes > 10 * 1024 * 1024) reasons.push("large_file");
  if (/invoice|receipt|bill/i.test(doc.canonicalName)) reasons.push("likely_invoice");

  const supportedMimeTypes = [
    "application/pdf",
    "image/",
    "text/",
    "application/vnd.openxmlformats",
    "application/zip",
    "application/json",
  ];
  const isSupported = supportedMimeTypes.some((t) => doc.mimeType.startsWith(t));
  if (!isSupported) reasons.push("unsupported");

  if (reasons.length === 0) return null;

  return {
    id: randomId(),
    documentId: doc.id,
    reason: reasons[0],
    status: "pending",
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export interface SyncProgress {
  stage: "messages" | "attachments" | "indexing" | "done" | "error" | "reconnect_required";
  messagesFound: number;
  attachmentsFound: number;
  attachmentsDownloaded: number;
  errors: number;
}

async function fetchMessagesIncremental(
  client: MailProviderClient,
  checkpoint?: string,
  maxAttachments = 100
): Promise<{
  messages: NormalizedMessage[];
  attachments: NormalizedAttachment[];
  nextCheckpoint?: string;
}> {
  const messages: NormalizedMessage[] = [];
  const attachments: NormalizedAttachment[] = [];

  if (client.provider === "gmail" && checkpoint) {
    const gmail = client as GmailClient;
    let pageToken: string | undefined;
    let lastHistoryId: string | undefined;
    do {
      const result = await gmail.listHistory(checkpoint, pageToken);
      messages.push(...result.messages);
      attachments.push(...result.attachments);
      lastHistoryId = result.historyId || lastHistoryId;
      pageToken = result.nextPageToken;
      if (attachments.length >= maxAttachments) break;
    } while (pageToken);
    return { messages, attachments, nextCheckpoint: lastHistoryId };
  }

  if (client.provider === "outlook" && checkpoint) {
    const outlook = client as OutlookClient;
    let pageToken: string | undefined;
    let lastDeltaToken: string | undefined;
    do {
      const result = await outlook.listDelta(checkpoint);
      messages.push(...result.messages);
      attachments.push(...result.attachments);
      lastDeltaToken = result.deltaToken || lastDeltaToken;
      pageToken = result.nextPageToken;
      if (attachments.length >= maxAttachments) break;
    } while (pageToken);
    return { messages, attachments, nextCheckpoint: lastDeltaToken };
  }

  let pageToken: string | undefined;
  do {
    const result = await client.listMessagesWithAttachments("has:attachment", pageToken);
    messages.push(...result.messages);
    attachments.push(...result.attachments);
    pageToken = result.nextPageToken;
    if (attachments.length >= maxAttachments) break;
  } while (pageToken);

  let nextCheckpoint: string | undefined;
  if (client.provider === "gmail") {
    try {
      const profile = await (client as GmailClient).getProfile();
      nextCheckpoint = profile.historyId;
    } catch { /* ignore */ }
  } else {
    try {
      const result = await (client as OutlookClient).listDelta();
      nextCheckpoint = result.deltaToken;
    } catch { /* ignore */ }
  }

  return { messages, attachments, nextCheckpoint };
}

export async function* syncProvider(
  client: MailProviderClient,
  accountId: string,
  maxAttachments = 100
): AsyncGenerator<SyncProgress, void, unknown> {
  const progress: SyncProgress = {
    stage: "messages",
    messagesFound: 0,
    attachmentsFound: 0,
    attachmentsDownloaded: 0,
    errors: 0,
  };

  const existingState = await db.syncState.where({ accountId }).and((s) => s.provider === client.provider).last();
  const checkpoint = existingState?.localCheckpoint || undefined;

  const syncStateId = existingState?.id || randomId();
  await db.syncState.put({
    id: syncStateId,
    accountId,
    provider: client.provider,
    status: "syncing",
    localCheckpoint: checkpoint,
    lastStartedAt: new Date().toISOString(),
  } as SyncState);

  yield { ...progress };

  let allMessages: NormalizedMessage[] = [];
  let allAttachments: NormalizedAttachment[] = [];
  let nextCheckpoint: string | undefined;

  try {
    const result = await fetchMessagesIncremental(client, checkpoint, maxAttachments);
    allMessages = result.messages;
    allAttachments = result.attachments;
    nextCheckpoint = result.nextCheckpoint;
    progress.messagesFound = allMessages.length;
    progress.attachmentsFound = allAttachments.length;
    yield { ...progress };
  } catch (err) {
    if (err instanceof ProviderError) {
      if (err.code === "token_expired") {
        progress.stage = "reconnect_required";
        await db.syncState.update(syncStateId, { status: "reconnect_required", errorCode: "token_expired" });
        yield { ...progress };
        return;
      }
      if (err.code === "rate_limited") {
        const match = err.message.match(/retry after (\d+)s/);
        const delayMs = match ? parseInt(match[1]) * 1000 : Math.min(30000, 1000 * Math.pow(2, progress.errors));
        progress.errors++;
        await sleep(delayMs);
      }
    }
    console.error("Sync error fetching messages:", err);
    progress.errors++;
    progress.stage = "error";
    await db.syncState.update(syncStateId, { status: "error", errorCode: "sync_failed" });
    yield { ...progress };
    return;
  }

  progress.stage = "attachments";
  yield { ...progress };

  const knownSenders = new Set((await db.occurrences.toArray()).map((o) => o.senderEmail));

  for (const att of allAttachments.slice(0, maxAttachments)) {
    try {
      const blob = await client.fetchAttachment(att.messageId, att.attachmentId);
      const contentHash = await hashBlob(blob);

      // Check for duplicate
      const duplicate = await findDuplicate(contentHash);
      const msg = allMessages.find((m) => m.id === att.messageId);

      if (duplicate) {
        // Add new occurrence pointing to existing document
        const occurrence: Occurrence = {
          id: randomId(),
          documentId: duplicate.id,
          provider: client.provider,
          accountId,
          providerMessageRef: att.messageId,
          providerAttachmentRef: att.attachmentId,
          senderEmail: msg?.senderEmail,
          senderName: msg?.senderName,
          subject: msg?.subject,
          receivedAt: msg?.receivedAt || new Date().toISOString(),
          threadRef: msg?.threadId,
          createdAt: new Date().toISOString(),
        };
        await db.occurrences.add(occurrence);

        // Create review item for duplicate
        const reviewItem = shouldCreateReviewItem(duplicate, msg?.senderEmail, true);
        if (reviewItem) await db.reviewItems.add(reviewItem);

        progress.attachmentsDownloaded++;
        yield { ...progress };
        continue;
      }

      // New document
      const docId = randomId();
      const opfsPath = await writeBlob(docId, blob);

      const doc: Document = {
        id: docId,
        canonicalName: att.filename,
        originalNames: [att.filename],
        mimeType: att.mimeType || getMimeType(att.filename),
        extension: getExtension(att.filename),
        sizeBytes: att.size || blob.size,
        contentHash,
        opfsPath,
        previewStatus: "pending",
        parseStatus: "none",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const occurrence: Occurrence = {
        id: randomId(),
        documentId: docId,
        provider: client.provider,
        accountId,
        providerMessageRef: att.messageId,
        providerAttachmentRef: att.attachmentId,
        senderEmail: msg?.senderEmail,
        senderName: msg?.senderName,
        subject: msg?.subject,
        receivedAt: msg?.receivedAt || new Date().toISOString(),
        threadRef: msg?.threadId,
        createdAt: new Date().toISOString(),
      };

      await db.documents.add(doc);
      await db.occurrences.add(occurrence);
      await db.opfsManifest.add({
        id: randomId(),
        documentId: docId,
        opfsPath,
        createdAt: new Date().toISOString(),
      });

      // Create review items for new documents
      const isNewSender = !!msg?.senderEmail && !knownSenders.has(msg.senderEmail);
      const reviewItem = shouldCreateReviewItem(doc, msg?.senderEmail, false) ||
        (isNewSender ? {
          id: randomId(),
          documentId: docId,
          reason: "new" as const,
          status: "pending",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } : null);
      if (reviewItem) await db.reviewItems.add(reviewItem);

      if (msg?.senderEmail) knownSenders.add(msg.senderEmail);

      progress.attachmentsDownloaded++;
      yield { ...progress };
    } catch (err) {
      if (err instanceof ProviderError && err.code === "token_expired") {
        progress.stage = "reconnect_required";
        await db.syncState.update(syncStateId, { status: "reconnect_required", errorCode: "token_expired" });
        yield { ...progress };
        return;
      }
      console.warn("Failed to download attachment", att, err);
      progress.errors++;
    }
  }

  progress.stage = "done";
  await db.syncState.update(syncStateId, {
    status: "idle",
    localCheckpoint: nextCheckpoint,
    lastCompletedAt: new Date().toISOString(),
  });
  yield { ...progress };
}

export async function clearLibrary(): Promise<void> {
  await db.documents.clear();
  await db.occurrences.clear();
  await db.collections.clear();
  await db.collectionItems.clear();
  await db.reviewItems.clear();
  await db.syncState.clear();
  await db.savedViews.clear();
  await db.opfsManifest.clear();

  try {
    const root = await navigator.storage.getDirectory();
    await root.removeEntry("attachdrive", { recursive: true });
  } catch { /* ignore */ }
}

export async function getLastSyncState(accountId: string, provider: "gmail" | "outlook"): Promise<SyncState | undefined> {
  return db.syncState.where({ accountId }).and((s) => s.provider === provider).last();
}
